import { cn } from "@/utils";
import type { ReactNode } from "react";
import { DataListHeader, type DataListHeaderProps } from "./DataListHeader";

/**
 * DataList — o padrão de dados tabulares da Fidexa.
 *
 * O problema: tabela de cobrança tem 6–8 colunas. Em 360px isso vira scroll
 * horizontal, e scroll horizontal dentro de página com scroll vertical é uma
 * das piores experiências possíveis em celular (o usuário perde a coluna de
 * referência e não descobre que há mais conteúdo à direita).
 *
 * A solução: a MESMA definição de colunas gera duas renderizações —
 * - `< lg`: uma lista de cartões, um por registro, com rótulo ao lado do valor;
 * - `>= lg`: a tabela de verdade, com cabeçalho fixo e zebra sutil.
 *
 * O corte é 1024px, e não 768px, por uma conta simples: a tabela tem 896px de
 * largura mínima (abaixo disso as colunas se esmagam). Cortando em 768px, o
 * tablet em retrato ficava com rolagem lateral dentro da rolagem vertical —
 * exatamente o que o DESIGN.md proíbe.
 *
 * Marque como `primary` a coluna que identifica a linha (nome do cliente) e
 * como `hiddenOnMobile` o que é acessório — no cartão ela some, não encolhe.
 */

export type DataListColumn<T> = {
  key: string;
  header: string;
  /** Identifica o registro. Vira o título do cartão em mobile. Use em uma coluna só. */
  primary?: boolean;
  /** Acessório: não aparece no cartão (abaixo de 1024px). */
  hiddenOnMobile?: boolean;
  /** Alinha à direita — use em toda coluna de valor/quantidade. */
  numeric?: boolean;
  /**
   * Proíbe a quebra em duas linhas. Use em documento, telefone, data e valor:
   * um CPF partido no meio deixa de ser lido como um número só.
   */
  noWrap?: boolean;
  /**
   * Corta o texto com reticências **só na tabela**, onde a largura é disputada
   * por todas as colunas — nome de cliente é o caso típico. No cartão do celular
   * o texto continua inteiro: lá a coluna é a tela toda e nada obriga a cortar.
   * Use `TruncatedText` no seu `render` para revelar o valor completo — nunca o
   * `title` do HTML, que o Design System aposentou.
   */
  truncate?: boolean;
  /**
   * Teto do corte quando `truncate` está ligado. `md` (224px) é o padrão; `sm`
   * (160px) para a coluna que precisa ceder largura às outras.
   */
  truncateWidth?: "sm" | "md" | "lg";
  render: (row: T) => ReactNode;
};

export interface DataListProps<T>
  extends Omit<DataListHeaderProps, "count" | "className"> {
  caption: string;
  columns: DataListColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Ações por registro (ver, cobrar). Ficam no rodapé do cartão em mobile. */
  rowActions?: (row: T) => ReactNode;
  /**
   * Título visível da coluna de ações. Sem ele o cabeçalho existe só para o
   * leitor de tela — o que basta quando a ação é óbvia pelo botão.
   */
  rowActionsHeader?: string;
  emptyState?: ReactNode;
  /**
   * A lista ocupa a altura que sobrou e **rola por dentro**, com a linha de
   * cabeçalho grudada no topo — só a partir de 1024px, onde a tabela existe.
   *
   * Serve para a tela em que a lista é o conteúdo: quem rola é ela. Em monitor
   * baixo o piso da seção faz a página rolar só o cabeçalho da tela para fora.
   * No toque não vale — rolagem dentro de rolagem é das piores
   * experiências ali, e abaixo de 1024px a lista já é de cartões.
   *
   * Exige um pai com altura definida, e o caminho é **flex**, não conta de
   * viewport:
   *
   * ```tsx
   * <section className="flex min-h-0 flex-col gap-4 lg:min-h-[min(36rem,var(--app-main-height))] lg:flex-1 lg:pb-6">
   *   <DataList fillHeight className="lg:min-h-0 lg:flex-1" … />
   *   <Pagination … />
   * </section>
   * ```
   *
   * O porquê do piso e do teto está em `fillHeight` no DESIGN.md.
   */
  fillHeight?: boolean;
  /**
   * Esconde a contagem ao lado do título. Use quando ela enganaria mais do que
   * informa — numa lista paginada sem total conhecido, "10" parece o resultado
   * inteiro quando são dez de milhares.
   */
  showCount?: boolean;
  /**
   * Abas (`Tabs`) que trocam a visão da lista. Ficam entre o cabeçalho e a
   * tabela: o título e os filtros são da tela, as abas recortam só as linhas.
   */
  tabs?: ReactNode;
  className?: string;
}

export function DataList<T>({
  caption,
  columns,
  rows,
  rowKey,
  rowActions,
  rowActionsHeader,
  emptyState,
  fillHeight = false,
  showCount = true,
  tabs,
  className,
  ...header
}: DataListProps<T>) {
  // O cabeçalho só existe se a tela pedir alguma de suas partes.
  const temCabecalho = Boolean(
    header.title ||
      header.onFilter ||
      header.onExport ||
      header.appliedFilters?.length
  );

  const cabecalho = temCabecalho ? (
    <DataListHeader
      {...header}
      count={showCount ? rows.length : undefined}
      // Com a lista preenchendo a tela, o cabeçalho gruda no topo: é dele que
      // saem "Filtros" e "Gerar planilha", e é nele que estão as etiquetas do
      // que está filtrado. Some daí, e o usuário olha uma lista curta sem
      // enxergar o filtro que a encurtou.
      className={
        fillHeight
          ? "lg:bg-ds-paper lg:sticky lg:top-0 lg:z-20 lg:py-2"
          : undefined
      }
    />
  ) : null;

  // Com filtro aplicado e nenhum resultado, o cabeçalho PRECISA continuar na
  // tela: é ele que mostra qual filtro esvaziou a lista. Some o cabeçalho junto
  // com a tabela e o usuário fica sem saída.
  if (rows.length === 0 && emptyState) {
    return (
      <div className={cn("flex flex-col gap-4", className)}>
        {cabecalho}
        {tabs}
        {emptyState}
      </div>
    );
  }

  const primary = columns.find(column => column.primary) ?? columns[0];
  const cardColumns = columns.filter(
    column => column !== primary && !column.hiddenOnMobile
  );

  return (
    <div
      className={cn(
        "flex flex-col gap-4",
        fillHeight && "lg:min-h-0",
        className
      )}
    >
      {cabecalho}
      {tabs}

      {/* Mobile — cartões */}
      <ul className="flex flex-col gap-3 lg:hidden" aria-label={caption}>
        {rows.map(row => (
          <li
            key={rowKey(row)}
            className="rounded-ds-lg border-ds-border bg-ds-surface border p-4"
          >
            <div className="font-ds-sans text-ds-body text-ds-ink-900 font-semibold">
              {primary.render(row)}
            </div>

            <dl className="mt-3 flex flex-col gap-2">
              {cardColumns.map(column => (
                <div
                  key={column.key}
                  className="flex items-baseline justify-between gap-4"
                >
                  <dt className="font-ds-sans text-ds-small text-ds-ink-600 shrink-0">
                    {column.header}
                  </dt>
                  {/* `min-w-0` + `break-words`: um valor sem espaço — UUID,
                      e-mail, URL — é uma palavra só, e sem isto a célula cresce
                      até caber a palavra inteira e empurra o cartão para fora
                      da tela. Medido em 390px: o identificador de 36 caracteres
                      levava a página a 441px de largura, e o celular inteiro
                      passava a rolar de lado — exatamente o que o cartão existe
                      para evitar. */}
                  <dd
                    className={cn(
                      "font-ds-sans text-ds-small text-ds-ink-900 min-w-0 text-right break-words",
                      column.numeric && "ds-numeric",
                      column.noWrap && "whitespace-nowrap"
                    )}
                  >
                    {column.render(row)}
                  </dd>
                </div>
              ))}
            </dl>

            {rowActions ? (
              <div className="border-ds-border mt-4 border-t pt-3">
                {rowActions(row)}
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {/* Desktop — tabela */}
      <div
        className={cn(
          // `relative`: conteúdo `sr-only` (que é `position: absolute`) dentro
          // das células precisa de um ancestral posicionado, senão se ancora no
          // documento e o estica para além da tela.
          "rounded-ds-lg border-ds-border relative hidden overflow-hidden border lg:block",
          // Em tela estreita a tabela rola na horizontal em vez de espremer as
          // colunas: comprimida, a loja quebra em cinco linhas e a última coluna
          // some sem avisar. Rolagem lateral aqui é do monitor pequeno — no
          // celular a lista já é de cartões, e lá ela continua proibida.
          "lg:overflow-x-auto",
          fillHeight && "lg:min-h-0 lg:flex-1 lg:overflow-y-auto"
        )}
      >
        <table
          className={cn(
            "bg-ds-surface w-full border-collapse",
            // Abaixo disso a tabela rola em vez de encolher. É a largura em que
            // as colunas ainda são legíveis com o documento em uma linha só.
            "min-w-4xl"
          )}
        >
          <caption className="sr-only">{caption}</caption>
          <thead>
            {/* A borda e o fundo moram no <th>, não no <tr>: grudado no topo,
                o <th> sai do fluxo e leva embora só o que é dele — a borda do
                <tr> rolaria junto com as linhas e o cabeçalho ficaria boiando
                sobre os dados. */}
            <tr>
              {columns.map(column => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    "font-ds-sans text-ds-caption text-ds-ink-600 border-ds-border bg-ds-ink-50 border-b px-4 py-3 font-semibold tracking-wide uppercase",
                    column.numeric ? "text-right" : "text-left",
                    fillHeight && "sticky top-0 z-10"
                  )}
                >
                  {column.header}
                </th>
              ))}
              {rowActions ? (
                <th
                  scope="col"
                  className={cn(
                    // À esquerda como as demais: título à direita sobre botões
                    // que começam à esquerda é desalinho puro.
                    "border-ds-border bg-ds-ink-50 border-b px-4 py-3 text-left",
                    // `w-px` encolhe a coluna até o conteúdo: em tabela de
                    // largura automática é o mínimo que vence, e a sobra de
                    // largura vai para as colunas de dado. Sem isso a coluna
                    // de ações herdava toda a folga da tabela e abria um vão
                    // de centenas de pixels à direita do botão.
                    "w-px whitespace-nowrap",
                    fillHeight && "sticky top-0 z-10"
                  )}
                >
                  {rowActionsHeader ? (
                    <span className="font-ds-sans text-ds-caption text-ds-ink-600 font-semibold tracking-wide uppercase">
                      {rowActionsHeader}
                    </span>
                  ) : (
                    <span className="sr-only">Ações</span>
                  )}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr
                key={rowKey(row)}
                className={cn(
                  "border-ds-border border-b last:border-b-0",
                  // Laranja claro da marca no hover: numa tabela de cobrança o
                  // olho precisa de uma âncora para não pular de linha ao
                  // percorrer as colunas da esquerda para a direita.
                  "ease-ds-out hover:bg-ds-brand-50 transition-colors duration-150",
                  "motion-reduce:transition-none"
                )}
              >
                {columns.map(column => (
                  <td
                    key={column.key}
                    className={cn(
                      "font-ds-sans text-ds-small text-ds-ink-900 px-4 py-3",
                      column.numeric && "ds-numeric text-right",
                      column.noWrap && "whitespace-nowrap",
                      column.primary && "font-medium"
                    )}
                  >
                    {/* O corte mora num bloco com largura máxima própria, e não
                        no <td>: em tabela de largura automática o max-width da
                        célula é só uma sugestão, e o navegador a estica assim
                        que o conteúdo pede. */}
                    {column.truncate ? (
                      <div
                        className={cn(
                          "truncate",
                          column.truncateWidth === "sm" && "max-w-40",
                          column.truncateWidth === "lg" && "max-w-72",
                          (column.truncateWidth ?? "md") === "md" && "max-w-56"
                        )}
                      >
                        {column.render(row)}
                      </div>
                    ) : (
                      column.render(row)
                    )}
                  </td>
                ))}
                {rowActions ? (
                  <td className="w-px px-4 py-3 text-left whitespace-nowrap">
                    {rowActions(row)}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
